// frontend/src/pages/Menu.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Menu.css';
import Shop from './employee/Shop';

const API_URL = process.env.REACT_APP_API_URL || 'https://moyobed.pro';

function Menu({ user, darkMode, toggleTheme }) {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [cartItems, setCartItems] = useState({});
  const [deadlineTimer, setDeadlineTimer] = useState('');
  const [showPinChange, setShowPinChange] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [pinForm, setPinForm] = useState({ old_pin: '', new_pin: '', confirm_pin: '' });
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const navigate = useNavigate();

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/menu`, {
        params: { company_id: user.company_id, employee_id: user.id }
      });
      console.log('📋 Меню получено:', response.data);
      setMenuData(response.data);
      
      // Синхронизируем cartItems с ответом API
      const syncedCart = {};
      response.data.forEach(day => {
        Object.entries(day.categories).forEach(([catName, dishes]) => {
          dishes.forEach(dish => {
            if (dish.is_ordered && dish.order_id) {
              syncedCart[dish.menu_item_id] = {
                orderId: dish.order_id,
                dishName: dish.dish_name,
                category: catName,
                date: day.date
              };
            }
          });
        });
      });
      setCartItems(syncedCart);
      
      if (response.data.length > 0 && !selectedDate) {
        setSelectedDate(response.data[0].date);
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки меню:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  // Таймер дедлайна
  useEffect(() => {
    if (!selectedDate || menuData.length === 0) return;
    
    const day = menuData.find(d => d.date === selectedDate);
    if (!day?.deadline) {
      setDeadlineTimer('');
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const deadline = new Date(day.deadline);
      const diff = deadline - now;

      if (diff <= 0) {
        setDeadlineTimer('⏰ Заказ закрыт');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setDeadlineTimer(`⏳ ${days}д ${hours}ч ${minutes}м до дедлайна`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [selectedDate, menuData]);

  const handleOrderToggle = async (menuItemId, dishName, isExpired, categoryName) => {
    if (isExpired) return;
    
    // Если блюдо уже выбрано — удаляем
    if (cartItems[menuItemId]) {
      try {
        await axios.delete(`${API_URL}/api/orders/${cartItems[menuItemId].orderId}`);
        await fetchMenu(); // Перезагружаем меню — cartItems обновится из API
      } catch (err) {
        console.error('❌ Ошибка удаления заказа:', err);
        await fetchMenu(); // Перезагружаем даже при ошибке
      }
      return;
    }
    
    // Проверяем, есть ли другой заказ в этой категории на этот день
    const existingInCategory = Object.entries(cartItems).find(
      ([id, item]) => item.category === categoryName && item.date === selectedDate
    );
    
    if (existingInCategory) {
      try {
        await axios.delete(`${API_URL}/api/orders/${existingInCategory[1].orderId}`);
      } catch (err) {
        console.error('❌ Ошибка удаления старого заказа:', err);
      }
    }
    
    // Создаём новый заказ
    try {
      const formData = new FormData();
      formData.append('employee_id', user.id);
      formData.append('company_id', user.company_id);
      formData.append('menu_item_id', menuItemId);

      await axios.post(`${API_URL}/api/orders`, formData);
      await fetchMenu(); // Перезагружаем меню — cartItems обновится из API
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка создания заказа');
      await fetchMenu(); // Перезагружаем даже при ошибке
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinSuccess('');
    
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      setPinError('PIN-коды не совпадают');
      return;
    }
    
    if (pinForm.new_pin.length !== 4 || !/^\d{4}$/.test(pinForm.new_pin)) {
      setPinError('PIN должен состоять из 4 цифр');
      return;
    }
    
    if (pinForm.old_pin === pinForm.new_pin) {
      setPinError('Новый PIN должен отличаться от старого');
      return;
    }
    
    setPinLoading(true);
    try {
      const formData = new FormData();
      formData.append('employee_id', user.id);
      formData.append('old_pin', pinForm.old_pin);
      formData.append('new_pin', pinForm.new_pin);
      
      await axios.post(`${API_URL}/api/auth/change-pin`, formData);
      setPinSuccess('✅ PIN-код успешно изменен!');
      setPinForm({ old_pin: '', new_pin: '', confirm_pin: '' });
      
      const updatedUser = { ...user, pin: pinForm.new_pin };
      sessionStorage.setItem('moyobed_user', JSON.stringify(updatedUser));
      
      setTimeout(() => {
        setShowPinChange(false);
        setPinSuccess('');
      }, 2000);
    } catch (err) {
      setPinError(err.response?.data?.detail || 'Ошибка при смене PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const goToCart = () => navigate('/cart');
  const goToComplaint = () => navigate('/complaint');

  const handleLogout = () => {
    sessionStorage.removeItem('moyobed_user');
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="page menu-page">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Загружаем меню...</p>
        </div>
      </div>
    );
  }

  const selectedDay = menuData.find(d => d.date === selectedDate);
  const cartCount = Object.keys(cartItems).length;

  return (
    <div className="page menu-page">
      <header className="menu-header">
        <div className="header-left">
          <h1 className="page-title">🍽️ Меню</h1>
          <p className="user-name">
            Привет, {user.last_name || user.full_name || 'Пользователь'}!
            {user.branch_name && <span className="branch-badge-menu"> 🏢 {user.branch_name}</span>}
          </p>
        </div>
        <div className="header-right" style={{ flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
          <button className="btn btn-sm btn-outline theme-toggle" onClick={toggleTheme}>
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button className="btn btn-sm btn-outline" onClick={() => setShowPinChange(true)}>
            🔑 Сменить PIN
          </button>
          <button className="btn btn-sm cart-btn" onClick={goToCart}>
            🛒 Корзина {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
          <button className="btn btn-sm btn-outline" onClick={goToComplaint}>
            💬 Жалоба
          </button>
          {user.shop_access && (
            <button className="btn btn-sm btn-outline" onClick={() => setShowShop(!showShop)}>
              🛍️ Магазин
            </button>
          )}
          <button className="btn btn-sm btn-outline" onClick={() => navigate('/profile')}>👤 Профиль</button>
          <button className="btn btn-sm btn-outline" onClick={handleLogout}>
            🚪 Выйти
          </button>
        </div>
      </header>

      {/* Модальное окно смены PIN */}
      {showPinChange && (
        <div className="modal-overlay" onClick={() => {
          if (!pinLoading) {
            setShowPinChange(false);
            setPinError('');
            setPinSuccess('');
            setPinForm({ old_pin: '', new_pin: '', confirm_pin: '' });
          }
        }}>
          <div className="modal-content pin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🔑 Смена PIN-кода</h3>
            <form onSubmit={handleChangePin}>
              <div className="form-group">
                <label>Текущий PIN</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Введите текущий PIN"
                  value={pinForm.old_pin}
                  onChange={(e) => setPinForm({...pinForm, old_pin: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                  maxLength="4"
                  required
                  disabled={pinLoading}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Новый PIN (4 цифры)</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Введите новый PIN"
                  value={pinForm.new_pin}
                  onChange={(e) => setPinForm({...pinForm, new_pin: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                  maxLength="4"
                  required
                  disabled={pinLoading}
                />
              </div>
              <div className="form-group">
                <label>Подтвердите новый PIN</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Повторите новый PIN"
                  value={pinForm.confirm_pin}
                  onChange={(e) => setPinForm({...pinForm, confirm_pin: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                  maxLength="4"
                  required
                  disabled={pinLoading}
                />
              </div>
              {pinError && <div className="error-message">{pinError}</div>}
              {pinSuccess && <div className="success-message">{pinSuccess}</div>}
              <div className="modal-actions">
                <button type="submit" className="btn" disabled={pinLoading}>
                  {pinLoading ? <span className="spinner"></span> : 'Изменить PIN'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => {
                  setShowPinChange(false);
                  setPinError('');
                  setPinSuccess('');
                  setPinForm({ old_pin: '', new_pin: '', confirm_pin: '' });
                }} disabled={pinLoading}>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Таймер дедлайна */}
      {deadlineTimer && (
        <div className={`deadline-banner ${deadlineTimer.includes('закрыт') ? 'expired' : ''}`}>
          {deadlineTimer}
        </div>
      )}

      {/* Выбор даты — горизонтальный скролл */}
      <div className="date-selector">
        {menuData.map(day => (
          <button
            key={day.date}
            className={`date-chip ${selectedDate === day.date ? 'active' : ''} ${day.is_expired ? 'expired-chip' : ''}`}
            onClick={() => setSelectedDate(day.date)}
          >
            <span className="date-day">{day.day_of_week.slice(0, 3)}</span>
            <span className="date-num">{day.date.slice(5)}</span>
            {day.is_expired && <span className="expired-dot">🔒</span>}
          </button>
        ))}
      </div>

      {/* Меню */}
      <div className="menu-content" style={{ paddingBottom: "80px" }}>
        {selectedDay && (
          Object.entries(selectedDay.categories).map(([catName, dishes]) => (
            <div key={catName} className="category-block card">
              <h2 className="category-title">{catName}</h2>
              <div className="dishes-grid">
                {dishes.map(dish => {
                  const isOrdered = cartItems[dish.menu_item_id] !== undefined;
                  return (
                    <div
                      key={dish.menu_item_id}
                      className={`dish-tile ${isOrdered ? 'ordered' : ''} ${dish.is_expired ? 'expired-dish' : ''}`}
                      onClick={() => handleOrderToggle(dish.menu_item_id, dish.dish_name, dish.is_expired, catName)}
                    >
                      <div className="dish-check">
                        {isOrdered ? '✅' : dish.is_expired ? '🔒' : '⬜'}
                      </div>
                      <div className="dish-name">{dish.dish_name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {menuData.length === 0 && (
        <div className="empty-state card">
          <p>😔 Меню пока не загружено</p>
          <p className="text-light">Обратитесь к администратору</p>
        </div>
      )}

      {/* Магазин */}
      {showShop && <Shop user={user} />}

      {/* Кнопка корзины внизу */}
      <nav className="bottom-nav">
        <button className="btn" onClick={goToCart}>
          🛒 Корзина ({cartCount})
        </button>
      </nav>
    </div>
  );
}

export default Menu;
